/**
 * EstimativaPorAgente — quadro do Gate B (SPEC A3 FR-22, AC-10).
 *
 * Uma linha por membro do elenco: agente, tier, custo estimado (US$) e as etapas sem
 * histórico. Custo null → "sem histórico" — NUNCA um número inventado. O total só soma o
 * que tem número e avisa quando há agente sem estimativa (sem clamp silencioso).
 *
 * Paleta C, estilos inline, pt-BR.
 */
import { C } from '@/theme/palette';
import { usd, rotuloDaEtapa, lista } from '@/lib/agentesDaExecucao';

const COR_DO_TIER = { opus: C.neon, sonnet: C.cyber, haiku: C.neutralDim };

function Tier({ tier }) {
  const cor = COR_DO_TIER[tier] ?? C.neutralDim;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
      color: cor, background: `${cor}18`, border: `1px solid ${cor}55`,
      textTransform: 'uppercase', letterSpacing: '0.04em',
    }}>
      {tier || '—'}
    </span>
  );
}

/**
 * @param {{ itens: Array<{agent_name:string, tier:string, custo_usd:number|null,
 *   etapas_sem_historico:string[]}>|undefined }} props
 */
export function EstimativaPorAgente({ itens }) {
  const linhas = lista(itens);
  if (!linhas.length) return null;

  const comNumero = linhas.filter((l) => usd(l.custo_usd) !== null);
  const semNumero = linhas.length - comNumero.length;
  const total = comNumero.reduce((acc, l) => acc + Number(l.custo_usd), 0);
  const cols = '1.6fr 0.7fr 1fr 2fr';

  return (
    <section aria-labelledby="estimativa-agente-heading" style={{ marginBottom: 28 }}>
      <h2
        id="estimativa-agente-heading"
        style={{
          fontSize: 12, fontWeight: 700, color: C.neutralMuted, letterSpacing: '0.08em',
          textTransform: 'uppercase', margin: '0 0 6px',
        }}
      >
        Estimativa por agente ({linhas.length})
      </h2>
      <div style={{ fontSize: 12, color: C.neutralMuted, marginBottom: 12, lineHeight: 1.5 }}>
        Média de tokens das etapas de cada agente nas últimas execuções concluídas × preço do
        tier. Etapa sem histórico não entra na conta — aparece listada.
      </div>

      <div
        role="table"
        aria-label="Estimativa de custo por agente"
        style={{ border: `1px solid ${C.blackBorder}`, borderRadius: 8, overflow: 'hidden' }}
      >
        <div role="row" style={{
          display: 'grid', gridTemplateColumns: cols, gap: 8, padding: '8px 14px',
          fontSize: 11, color: C.neutralMuted, background: C.blackElev2,
        }}>
          <div role="columnheader">Agente</div>
          <div role="columnheader">Tier</div>
          <div role="columnheader">Custo estimado</div>
          <div role="columnheader">Etapas sem histórico</div>
        </div>
        {linhas.map((l, i) => {
          const custo = usd(l.custo_usd);
          const semHist = Array.isArray(l.etapas_sem_historico) ? l.etapas_sem_historico : [];
          return (
            <div
              role="row"
              data-testid="linha-estimativa-agente"
              key={`${l.agent_name}-${i}`}
              style={{
                display: 'grid', gridTemplateColumns: cols, gap: 8, padding: '10px 14px',
                fontSize: 13, alignItems: 'center', borderTop: `1px solid ${C.blackBorder}`,
                background: C.blackElev,
              }}
            >
              <div role="cell" style={{ color: C.neutral, fontWeight: 600 }}>{l.agent_name || '—'}</div>
              <div role="cell"><Tier tier={l.tier} /></div>
              <div role="cell" style={{ fontVariantNumeric: 'tabular-nums',
                color: custo === null ? C.warn : C.neutral }}>
                {custo ?? 'sem histórico'}
              </div>
              <div role="cell" style={{ fontSize: 12, color: semHist.length ? C.warn : C.neutralMuted }}>
                {semHist.length ? semHist.map(rotuloDaEtapa).join(', ') : '—'}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 8, fontSize: 12, color: C.neutralDim }}>
        Total estimado: <strong style={{ color: C.neutral }}>{usd(total)}</strong>
        {semNumero > 0 && (
          <span style={{ color: C.warn }}>
            {' '}· {semNumero} agente(s) sem histórico fora da soma
          </span>
        )}
      </div>
    </section>
  );
}

export default EstimativaPorAgente;
